/**
 * The page's side of a chosen leave (ADR-084's amendment of 2026-10-13, PROTOCOL.md 6.65.0): the game menu's Change
 * character and Log out ask the gateway before the page leaves, and the menu asks while its character is in combat so
 * that it can say how long. The desk sends one `leave` at a time and settles with the gateway's `leaveAnswer`, or
 * with nothing when the frame could not be sent, the connection ended first, or no answer came in time. It holds no
 * socket and no clock of its own, so it is tested without a page.
 */

/**
 * The gateway's answer: `lock`, the ticks left of the character's in-combat window, which refuse a leave while above
 * 0, and `stay`, the ticks the character stays in the world once it leaves.
 */
export interface LeaveAnswer {
  readonly lock: number;
  readonly stay: number;
}

/**
 * How long the desk waits for an answer, in milliseconds. The gateway answers in the turn the frame arrives, so this
 * bounds only a connection that has stopped carrying frames without closing.
 */
export const LEAVE_ANSWER_MS = 3000;

/**
 * What the desk needs: `send` puts a `leave` on the connection and says whether it went; `left` is told, in the same
 * turn the answer arrives and before the question settles, that the gateway took a confirmed leave, whose close
 * follows; `schedule` runs a function after a delay and returns its cancel, the page's timer unless given.
 */
export interface LeaveDeskOptions {
  readonly send: (confirm: boolean) => boolean;
  readonly left: () => void;
  readonly schedule?: (delayMs: number, run: () => void) => () => void;
}

/**
 * A timer of the page's.
 *
 * @param delayMs The delay.
 * @param run What to run.
 * @returns Its cancel.
 */
function afterDelay(delayMs: number, run: () => void): () => void {
  const timer = setTimeout(run, delayMs);
  return () => clearTimeout(timer);
}

/**
 * One question out at a time, settled by the next answer.
 */
export class LeaveDesk {
  private pending: { readonly confirm: boolean; readonly settle: (answer: LeaveAnswer | null) => void; readonly cancel: () => void } | null = null;

  /**
   * @param options What the desk needs.
   */
  public constructor(private readonly options: LeaveDeskOptions) {}

  /**
   * Asks what a leave now would meet, or leaves.
   *
   * @param confirm False to ask and change nothing, true to leave.
   * @returns The answer; null when a question is already out, the frame was not sent, the connection ended before
   *   the answer, or none came within `LEAVE_ANSWER_MS`. It never rejects.
   */
  public ask(confirm: boolean): Promise<LeaveAnswer | null> {
    if (this.pending !== null || !this.options.send(confirm)) {
      return Promise.resolve(null);
    }
    return new Promise<LeaveAnswer | null>((resolve) => {
      const cancel = (this.options.schedule ?? afterDelay)(LEAVE_ANSWER_MS, () => this.settle(null));
      this.pending = { confirm, settle: resolve, cancel };
    });
  }

  /**
   * Takes a `leaveAnswer`. One that takes a confirmed leave tells `left` first, so the page has let its connection
   * go before the gateway's close arrives. An answer nobody asked for is dropped.
   *
   * @param answer The answer.
   */
  public answered(answer: LeaveAnswer): void {
    const pending = this.pending;
    if (pending === null) {
      return;
    }
    if (pending.confirm && answer.lock === 0) {
      this.options.left();
    }
    this.settle(answer);
  }

  /**
   * The connection ended: a question still out settles with nothing.
   */
  public ended(): void {
    this.settle(null);
  }

  /**
   * Settles the question that is out, if one is.
   *
   * @param answer The answer, or null for none.
   */
  private settle(answer: LeaveAnswer | null): void {
    const pending = this.pending;
    if (pending === null) {
      return;
    }
    this.pending = null;
    pending.cancel();
    pending.settle(answer);
  }
}
